import {
  Button,
  toggleButtonClasses,
  ToggleButtonGroup,
  toggleButtonGroupClasses,
  Typography,
} from '@montugroup/design-system';
import type { Theme } from '@mui/material';
import { alpha, FormLabel, styled } from '@mui/material';

// the height of the waitlist's buttons and options, shared so they line up
export const waitlistControlHeight = (theme: Theme) => theme.spacing(13);

// cast back to FormLabel because styled() drops the `component` prop, which renders it as the fieldset's legend
export const AvailabilityLabel = styled(FormLabel)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.text.primary,
})) as typeof FormLabel;

// The export's `_states` tokens aren't in MUI's types, and its `var(--mui-…)` names don't exist under the theme's
// `--alternaleaf-` prefix, so read the values; themes without them (e.g. in tests) get the export's opacities.
const primaryStates = (theme: Theme) => {
  const { _states } = theme.palette.primary as Theme['palette']['primary'] & {
    _states?: { active?: string; outlinedBorder?: string };
  };
  return {
    active: _states?.active ?? alpha(theme.palette.primary.main, 0.56),
    outlinedBorder: _states?.outlinedBorder ?? alpha(theme.palette.primary.main, 0.5),
  };
};

// The options stack as full-width rows, with the chosen one filled in the brand purple. `&&` outweighs MUI's
// grouped-button rules, which join the buttons' borders and square off their corners.
export const AvailabilityToggleGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  [`&& .${toggleButtonGroupClasses.grouped}`]: {
    margin: 0,
    height: waitlistControlHeight(theme),
    padding: theme.spacing(0, 4),
    justifyContent: 'flex-start',
    textAlign: 'left',
    textTransform: 'none',
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: theme.typography.body2.lineHeight,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
    // the theme's outlined primary button hover (Alternaleaf export, MuiButton outlinedPrimary), as on No thanks
    [`&:not(.${toggleButtonClasses.selected}):hover`]: {
      color: theme.palette.primary.main,
      borderColor: primaryStates(theme).outlinedBorder,
      backgroundColor: primaryStates(theme).active,
    },
  },
  [`&& .${toggleButtonGroupClasses.grouped}.${toggleButtonClasses.selected}`]: {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
  },
}));

export const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  lineHeight: 1.6,
  color: theme.palette.text.secondary,
}));

export const FormActions = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  width: '100%',
}));

// the buttons share the row equally
export const ActionButton = styled(Button)(({ theme }) => ({
  flex: 1,
  margin: 0,
  height: waitlistControlHeight(theme),
}));
